import { type ReactNode } from 'react'
import {
  ArrowRight,
  BellRing,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  DollarSign,
  TrendingUp,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useRailScroll } from '@/hooks/useRailScroll'
import {
  APP_STORE_URL,
  PANEL_GRADIENT,
  PLAY_STORE_URL,
  whatsappHref,
} from './publicShell'
import WhatsAppLink from './WhatsAppLink'

const HEADING_ID = 'app-showcase-heading'
const RAIL_ID = 'app-showcase-rail'

/**
 * Estado e ações de um trilho horizontal com rolagem nativa.
 *
 * Sem biblioteca de carrossel: o scroll é do navegador (com scroll-snap), e daqui
 * saem só as três coisas que o CSS não dá — se ainda dá para andar em cada
 * direção, o passo das setas, e arrastar com o mouse.
 *
 * O arrasto só intercepta mouse: em touch e caneta o scroll nativo já resolve, e
 * interceptar ali só atrapalharia. O `onClickCapture` engole o clique que sairia
 * logo depois de um arrasto, senão soltar o botão em cima de um link do painel
 * abriria esse link.
 */
interface ShowcaseHighlight {
  icon: LucideIcon
  label: string
}

/**
 * Ação do banner.
 *
 * O app do aluno se baixa na loja; o painel do administrador, não — é web, e quem
 * o vê aqui ainda não é cliente. Ali o passo seguinte é falar com a gente, então o
 * banner leva ao WhatsApp em vez de a uma loja que não teria o que instalar.
 */
type ShowcaseCta =
  { kind: 'stores' } | { kind: 'contact'; label: string; message: string }

interface ShowcaseCard {
  /** Chave da lista e âncora dos controles. */
  id: string
  /** Rótulo pequeno acima do título ("APLICATIVO ALUNO"). */
  eyebrow: string
  title: string
  description: string
  highlights: ShowcaseHighlight[]
  cta: ShowcaseCta
  image: { src: string; alt: string }
}

/**
 * Conteúdo dos slides.
 *
 * Acrescentar um slide é acrescentar um objeto aqui — o resto do componente não
 * muda, e as setas somem sozinhas se sobrar um card só.
 */
const SHOWCASE_CARDS: ShowcaseCard[] = [
  {
    id: 'aluno',
    eyebrow: 'Aplicativo aluno',
    title: 'Melhor experiência para seu aluno',
    description:
      'Agende suas aulas, acompanhe os créditos do pacote e receba lembretes automáticos — tudo pelo app. Sua escola de surf, na palma da mão.',
    highlights: [
      { icon: DollarSign, label: 'Pacotes e créditos sempre à vista' },
      { icon: CircleCheck, label: 'Agende sua aula em segundos' },
      { icon: BellRing, label: 'Lembretes automáticos de aula' },
    ],
    cta: { kind: 'stores' },
    image: {
      src: '/lp/app-aluno.webp',
      alt: 'Mão segurando um celular com o app da BeWave aberto na tela inicial do aluno',
    },
  },
  {
    id: 'admin',
    eyebrow: 'Painel do administrador',
    title: 'Visão completa de quem administra',
    description:
      'Agenda, equipe e financeiro num painel só. Veja o que já entrou, o que falta receber e o que vai acontecer na semana — de onde estiver.',
    highlights: [
      { icon: CalendarCheck, label: 'Agenda e turmas sob controle' },
      { icon: TrendingUp, label: 'Financeiro em tempo real' },
      { icon: Users, label: 'Equipe e permissões' },
    ],
    cta: {
      kind: 'contact',
      label: 'Agendar uma reunião',
      message:
        'Olá! Quero agendar uma reunião para conhecer o painel da BeWave para a minha escola.',
    },
    image: {
      src: '/lp/app-admin.webp',
      alt: 'Notebook com o painel administrativo da BeWave aberto nas configurações da escola',
    },
  },
]

/**
 * Seção do app na landing: painel azul com a chamada de um lado e a foto do
 * app do outro, num trilho horizontal estilo Netflix.
 *
 * A rolagem é nativa (overflow + scroll-snap), não uma biblioteca de carrossel.
 * O caminho pelo embla (`components/ui/carousel`) foi tentado e abandonado: as
 * opções dele decidem sozinhas onde o trilho pode parar — o padrão `trimSnaps`
 * travava o último banner encostado na borda direita, com o primeiro ainda
 * ocupando um quinto da tela. Com scroll nativo quem manda é o gutter do
 * `public-rail`, que serve aos dois lados de uma vez, e ainda saem de cena três
 * stubs de jsdom que o embla exigia só para montar no teste.
 *
 * Mesma escolha que `SchoolPhotos` fez, por outro motivo (lá, o custo de um
 * embla por card numa lista longa).
 */
export default function AppShowcaseCarousel() {
  const { ref, dragProps, canScrollPrev, canScrollNext, scrollByPage } =
    useRailScroll<HTMLUListElement>({ id: RAIL_ID })

  // Com um banner só não há o que navegar, e as setas ficariam as duas
  // desabilitadas em cima do card.
  const hasControls = SHOWCASE_CARDS.length > 1

  return (
    <section className="py-12 lg:py-20" aria-labelledby={HEADING_ID}>
      <h2 id={HEADING_ID} className="sr-only">
        Os aplicativos da BeWave
      </h2>

      <div className="relative">
        {/* O trilho é o próprio scroller: rolagem nativa com scroll-snap, sem
            biblioteca. `public-rail` (application.css) põe o gutter dos dois
            lados e repete a conta em scroll-padding-inline, que é o que faz o
            primeiro banner nascer alinhado com o header e o último conseguir
            parar nesse mesmo alinhamento em vez de travar na borda. */}
        <ul
          ref={ref}
          {...dragProps}
          aria-label="Banners dos aplicativos"
          className="public-rail scrollbar-hide flex snap-x snap-mandatory select-none gap-4 overflow-x-auto scroll-smooth lg:gap-6"
        >
          {SHOWCASE_CARDS.map((card) => (
            // Menos que 100%: a sobra do próximo aparecendo na borda é o que
            // conta que há mais banners. `flex` no <li> estica o card até a
            // altura do item mais alto — é o que iguala a altura dos dois.
            <li
              key={card.id}
              className="flex w-[92%] shrink-0 snap-start sm:w-[86%] lg:w-[70%]"
            >
              <ShowcaseSlide card={card} />
            </li>
          ))}
        </ul>

        {hasControls && (
          // As setas flutuam por cima, nas pontas do trilho. Some a que não tem
          // para onde ir, em vez de ficar desabilitada ocupando espaço.
          <div className="pointer-events-none absolute inset-0 hidden items-center justify-between px-4 sm:flex">
            <RailArrow
              direction="prev"
              disabled={!canScrollPrev}
              onClick={() => scrollByPage(-1)}
            />
            <RailArrow
              direction="next"
              disabled={!canScrollNext}
              onClick={() => scrollByPage(1)}
            />
          </div>
        )}
      </div>
    </section>
  )
}

function RailArrow({
  direction,
  disabled,
  onClick,
}: {
  direction: 'prev' | 'next'
  disabled: boolean
  onClick: () => void
}) {
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'prev' ? 'Banner anterior' : 'Próximo banner'}
      title={direction === 'prev' ? 'Banner anterior' : 'Próximo banner'}
      aria-controls={RAIL_ID}
      className={cn(
        'pointer-events-auto flex size-11 items-center justify-center rounded-full bg-white text-gray-800 shadow-lg transition hover:bg-gray-50 active:scale-90',
        'disabled:pointer-events-none disabled:opacity-0',
      )}
    >
      <Icon className="size-5" aria-hidden="true" />
    </button>
  )
}

function ShowcaseSlide({ card }: { card: ShowcaseCard }) {
  return (
    // `w-full` porque agora o card é filho flex do <li>; `h-full` é o que iguala a
    // altura dos banners — o <li> já é esticado até a altura do mais alto, mas este
    // grid nascia com altura automática e parava na altura da própria copy. No
    // mobile a foto (linha `1fr`) absorve a sobra, senão o banner mais curto
    // ficaria com um buraco embaixo.
    <div className="grid h-full w-full grid-rows-[auto_1fr] gap-4 lg:min-h-[440px] lg:grid-cols-2 lg:grid-rows-1 lg:gap-6">
      <div
        className="flex flex-col justify-center rounded-[40px] p-8 sm:p-9 lg:p-10"
        style={{ background: PANEL_GRADIENT }}
      >
        <span className="w-fit rounded-full bg-white/20 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.13em] text-white">
          {card.eyebrow}
        </span>

        <h2 className="mt-6 text-[1.75rem] font-bold leading-[1.08] tracking-tight text-white sm:text-[2.125rem] lg:text-[2.25rem]">
          {card.title}
        </h2>

        <p className="mt-4 max-w-[30rem] text-[0.9375rem] leading-relaxed text-white/80 sm:text-base">
          {card.description}
        </p>

        <ul className="mt-6 space-y-2.5">
          {card.highlights.map(({ icon: Icon, label }) => (
            <li key={label} className="flex items-center gap-3">
              <span
                className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white/15"
                aria-hidden="true"
              >
                <Icon className="size-3.5 text-white" />
              </span>
              <span className="text-[0.9375rem] text-white/95">{label}</span>
            </li>
          ))}
        </ul>

        <div className="mt-7 flex flex-wrap gap-3">
          {card.cta.kind === 'stores' ? (
            <>
              <PanelLink href={PLAY_STORE_URL}>
                <GooglePlayGlyph />
                Google Play
              </PanelLink>
              <PanelLink href={APP_STORE_URL}>
                <AppleGlyph />
                App Store
              </PanelLink>
            </>
          ) : (
            <WhatsAppLink
              href={whatsappHref(card.cta.message)}
              source="app_showcase"
              listingType="bewave"
              className={PANEL_LINK_CLASS}
            >
              {card.cta.label}
              <ArrowRight className="size-[18px] shrink-0" aria-hidden="true" />
            </WhatsAppLink>
          )}
        </div>
      </div>

      {/* A altura vem da linha do grid, então a foto acompanha o painel; o
          `min-h` é o piso para quando não sobra altura para distribuir. */}
      <img
        src={card.image.src}
        alt={card.image.alt}
        loading="lazy"
        className="h-full min-h-64 w-full rounded-[40px] object-cover sm:min-h-80 lg:min-h-0"
      />
    </div>
  )
}

/**
 * Botão branco do painel: as lojas do app e o CTA de contato usam o mesmo, para
 * os dois banners terminarem com a mesma peça no mesmo lugar.
 *
 * A ordem de ícone e texto fica com quem chama — nas lojas a marca vem antes do
 * nome, no CTA a seta vem depois da frase.
 */
/** A aparência é compartilhada com o CTA de WhatsApp, que usa WhatsAppLink. */
const PANEL_LINK_CLASS =
  'inline-flex items-center gap-2.5 rounded-xl bg-white px-6 py-3.5 text-[0.9375rem] font-semibold text-lp-dark transition-transform hover:scale-[1.02] active:scale-[0.98]'

function PanelLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={PANEL_LINK_CLASS}
    >
      {children}
    </a>
  )
}

/* As marcas das lojas não existem no lucide — icon set genérico não carrega logo
   de terceiro. Herdam a cor do botão via `currentColor`. */

function AppleGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-[18px] shrink-0"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 14.25 3.51 5.31 9.05 5.03c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.36zM12.03 4.97C11.88 2.73 13.7.88 15.79.7c.29 2.58-2.34 4.5-3.76 4.27z" />
    </svg>
  )
}

function GooglePlayGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-[18px] shrink-0"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M3.06 2.17a1.5 1.5 0 0 0-.56 1.17v17.32c0 .47.21.89.56 1.17l9.44-9.83-9.44-9.83zM16.1 8.05 4.83 2.12c-.73-.42-1.38-.37-1.77.05l9.44 9.83 3.6-3.95zM20.27 10.42l-3.32-1.75-3.83 3.94 3.83 3.94 3.32-1.75c1.19-.67 1.19-1.71 0-2.38zM3.06 21.83c.39.42 1.04.47 1.77.05l11.27-5.93-3.6-3.95-9.44 9.83z" />
    </svg>
  )
}
